import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import PropTypes from 'prop-types';

import {
  AccountManager,
  ApiManager,
  BankAccountsApi,
  Bus,
  constants,
  getMaintenanceByMode,
  isUserNeedKyc,
  isUserUnderVerification,
  Logger,
  needsBankAccounts,
  NotificationManager,
  SiteConfigManager,
  useApplicationState,
  useSettings,
} from '@atlas/core';
import { Maintenance } from '@atlas/ui';

import { LocalConfig } from 'common/utils/utils';
import Logo from 'common/Widgets/Logo/Logo';
import { useSelfRegulationContext } from 'src/components/SelfRegulationProvider';
import { getCurrentCulture } from 'src/components/Sportsbook/altenar/model/getCurrentCulture';
import {
  goToAccount,
  goToAccountNumbersPage,
  goToRealityCheck,
  goToSelfRegulation,
} from 'src/helpers/verification';

import { buildPathFromRoute, parsePathToPageProps } from './model/altenarRoutes';
import { loadAltenarScript } from './model/loadAltenarScript';
import { signIn } from './model/signIn';
import { useAltenarRoutes } from './model/useAltenarRoutes';
import { useUserSettings } from './model/useUserSettings';

const CONTAINER_ID = 'template-altenar-container';
const {
  MAINTENANCE_MODE: { SPORTSBOOK },
} = constants;

const ensureAltenarLoaded = async () => {
  if (typeof window === 'undefined' || window.altenarWSDK) return;
  await loadAltenarScript();
};

const fetchAltenarToken = async () => {
  const authToken = AccountManager.getToken() || ApiManager.getAuthorizationToken();
  const url = new URL('betting/client/aa/client-token/create', ApiManager.getHostname('core'));

  //using fetch cause response type text, not json
  const response = await fetch(url.href, {
    headers: {
      ...ApiManager.getDefaultHeaders(),
      ...(authToken ? { Authorization: `Bearer ${authToken}` } : {}),
    },
    method: 'GET',
    mode: 'cors',
  });
  if (!response.ok) throw new Error(`Altenar token request failed: ${response.status}`);
  const rawText = (await response.text()).trim();
  if (rawText.startsWith('"') && rawText.endsWith('"')) {
    try {
      return JSON.parse(rawText);
    } catch {
      return rawText.slice(1, -1);
    }
  }
  return rawText;
};

const getInitialPageProps = (pathname, search, getPageByPath) => {
  const parsed = parsePathToPageProps(pathname, search);
  if (parsed) return parsed;
  const fallbackPage = getPageByPath(pathname);
  return fallbackPage ? { page: fallbackPage } : {};
};

const isVerificationDataResolved = account =>
  Boolean(account?.kycVerificationDate || account?.verificationUri);

const isBankAccountStepSkippable = () =>
  Boolean(SiteConfigManager.getConfig('config.config.bankAccountStep.isSkippable'));

const AltenarSportsbook = ({ culture, integration }) => {
  const containerRef = useRef(null);
  const sportsBookRef = useRef(null);
  const verificationPopupShownRef = useRef(false);
  const bankAccountsConfirmedRef = useRef(false);
  const lastTokenAuthRef = useRef(null);
  const [isInitialized, setIsInitialized] = useState(false);
  const [compliancePassed, setCompliancePassed] = useState(false);
  const { getPageByPath, goBack, goTo, pushUrl } = useAltenarRoutes();
  const { pathname, search } = useLocation();
  const { lang } = useSettings();
  const { account, authenticated, bankAccounts, resolved } = useApplicationState();
  const { showSelfRegulationPopupIfNeeded, showSpentLimitPopupIfNeeded } =
    useSelfRegulationContext();

  const projectId = LocalConfig.get('projectId');
  const maintenance = useMemo(() => getMaintenanceByMode(SPORTSBOOK, projectId), [projectId]);

  // Bus handlers and the post-init refresh need the latest route values
  // without re-subscribing each render.
  const routesRef = useRef({ getPageByPath, goBack, goTo, pathname, pushUrl, search });
  routesRef.current = { getPageByPath, goBack, goTo, pathname, pushUrl, search };

  const authStateRef = useRef({ authenticated, resolved });
  authStateRef.current = { authenticated, resolved };

  const authBoundaryRef = useRef(authenticated);

  const mountSportsBook = () => {
    if (!window.altenarWSDK || !containerRef.current) return null;
    const initialProps = getInitialPageProps(
      routesRef.current.pathname,
      routesRef.current.search,
      routesRef.current.getPageByPath,
    );

    return window.altenarWSDK.addSportsBook({
      container: containerRef.current,
      props: {
        onBetHistoryButtonClick: event => {
          Logger.info('onBetHistoryButtonClick>>>', event);
        },
        onBetPlacementError: message => {
          Logger.error('onBetPlacementError>>>', message);
          if (!message) return;
          NotificationManager.display({
            data: { rawText: true },
            message,
            status: 'error',
            type: 'notification',
          });
        },
        onGoBack: () => {
          routesRef.current.goBack?.();
        },
        onNavigationButtonClick: ({ page }) => {
          routesRef.current.goTo(page);
        },
        onRouteChangeDetailed: detail => {
          const url = buildPathFromRoute(detail);
          if (!url) return;
          const current = `${routesRef.current.pathname}${routesRef.current.search || ''}`;
          if (url === current) return;
          routesRef.current.pushUrl?.(url);
          // SDK uses memory router, so in-widget navigation never resets the host
          // page scroll. Snap the layout-wrapper back to the top, matching native SB behavior.
          Bus.emit('ui:scroll:content', { behavior: 'auto', scrollTo: 0 });
        },
        onSignInButtonClick: () => {
          signIn();
        },
        routerType: 'memory',
        ...initialProps,
      },
    });
  };

  const refreshAuth = () => {
    if (lastTokenAuthRef.current === true) return;
    lastTokenAuthRef.current = true;
    fetchAltenarToken()
      .then(token => {
        window.altenarWSDK?.set({ token });
      })
      .catch(error => {
        lastTokenAuthRef.current = false;
        Logger.error('Altenar token fetch failed', error);
      });
  };

  useEffect(() => {
    if (!isInitialized) return;
    if (!resolved || !authenticated) return;
    if (lastTokenAuthRef.current === authenticated) return;

    refreshAuth();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authenticated, resolved, isInitialized]);

  useEffect(() => {
    const handleLogout = () => {
      window.altenarWSDK?.set({ token: '' });
      lastTokenAuthRef.current = false;
    };

    const handleLogin = () => {
      refreshAuth();
    };

    Bus.on(Bus.events.auth.logout, handleLogout);
    Bus.on(Bus.events.auth.login, handleLogin);
    Bus.on(Bus.events.auth.simpleLogin, handleLogin);

    return () => {
      Bus.off(Bus.events.auth.logout, handleLogout);
      Bus.off(Bus.events.auth.login, handleLogin);
      Bus.off(Bus.events.auth.simpleLogin, handleLogin);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!isInitialized) return undefined;

    const handlePopState = () => {
      if (!window.altenarWSDK) return;
      const props = parsePathToPageProps(window.location.pathname, window.location.search);
      if (!props) return;
      window.altenarWSDK.set(props);
    };

    window.addEventListener('popstate', handlePopState);

    return () => window.removeEventListener('popstate', handlePopState);
  }, [isInitialized]);

  const showVerificationPopup = onClose => {
    if (verificationPopupShownRef.current) return;
    verificationPopupShownRef.current = true;
    NotificationManager.display({
      data: { onClose },
      message: 'verificationPopup',
      type: 'verificationPopup',
    });
  };

  useEffect(() => {
    if (authBoundaryRef.current === authenticated) return undefined;
    authBoundaryRef.current = authenticated;
    verificationPopupShownRef.current = false;
    bankAccountsConfirmedRef.current = false;
    setCompliancePassed(false);
    return undefined;
  }, [authenticated]);

  useEffect(() => {
    if (maintenance || !resolved || compliancePassed) return undefined;

    let cancelled = false;

    const runComplianceChecks = async () => {
      if (!isUserUnderVerification(account)) return true;
      if (!isVerificationDataResolved(account)) return false;

      if (isUserNeedKyc(account)) {
        showVerificationPopup(goToAccount);
        return false;
      }

      if (!isBankAccountStepSkippable()) {
        let bankAccountsMissing = needsBankAccounts(bankAccounts);

        if (bankAccountsMissing && !bankAccountsConfirmedRef.current) {
          try {
            const accounts = await BankAccountsApi.getBankAccounts();
            if (cancelled) return false;
            bankAccountsConfirmedRef.current = true;
            bankAccountsMissing = needsBankAccounts({
              data: accounts || [],
              hasSessionUpdate: false,
            });
          } catch (error) {
            Logger.error('Altenar bank accounts fetch failed', error);
          }
        }

        if (bankAccountsMissing) {
          showVerificationPopup(goToAccountNumbersPage);
          return false;
        }
      }

      const canProceedSpentLimit = await showSpentLimitPopupIfNeeded();
      if (cancelled) return false;
      if (!canProceedSpentLimit) {
        goToSelfRegulation();
        return false;
      }

      const canProceedSelfRegulation = await showSelfRegulationPopupIfNeeded();
      if (cancelled) return false;
      if (!canProceedSelfRegulation) {
        goToRealityCheck();
        return false;
      }

      return true;
    };

    runComplianceChecks().then(passed => {
      if (cancelled) return;
      if (passed) {
        setCompliancePassed(true);
        return;
      }
      if (sportsBookRef.current) {
        sportsBookRef.current.remove?.();
        sportsBookRef.current = null;
        lastTokenAuthRef.current = null;
        setIsInitialized(false);
      }
    });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [account, bankAccounts, compliancePassed, maintenance, resolved]);

  useEffect(() => {
    if (!compliancePassed || isInitialized) return undefined;

    let cancelled = false;

    const initAltenar = async () => {
      await ensureAltenarLoaded();
      if (cancelled || !window.altenarWSDK) return;

      let token;
      if (authStateRef.current.authenticated) {
        try {
          token = await fetchAltenarToken();
        } catch (error) {
          Logger.error('Altenar token fetch failed', error);
        }
      }
      if (cancelled || !authStateRef.current.authenticated) token = undefined;
      if (cancelled) return;

      if (window.__altenarHostInitialized) {
        window.altenarWSDK.set({ token: token || '' });
      } else {
        window.altenarWSDK.init({
          culture: getCurrentCulture(culture, lang),
          integration,
          ...(token ? { token } : {}),
        });
        window.__altenarHostInitialized = true;
      }

      sportsBookRef.current = mountSportsBook();

      lastTokenAuthRef.current = Boolean(token);
      setIsInitialized(true);
    };

    initAltenar();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [compliancePassed, isInitialized]);

  useEffect(
    () => () => {
      sportsBookRef.current?.remove?.();
      sportsBookRef.current = null;
    },
    [],
  );

  useUserSettings();

  if (maintenance) {
    return <Maintenance endAt={maintenance.end_at} logo={<Logo />} type={SPORTSBOOK} />;
  }

  return <div className="viewport-height page__main" id={CONTAINER_ID} ref={containerRef} />;
};

AltenarSportsbook.propTypes = {
  culture: PropTypes.string.isRequired,
  integration: PropTypes.string.isRequired,
};

export default AltenarSportsbook;
